import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { ClientEntityType } from "@/lib/supabase/supabaseTypes";
import { formatClientNameForSidebar } from "@/lib/utils/client-utils";
import { ClientInitialsAvatar } from "./ClientInitialsAvatar";
import { ClientPreviewHoverCard } from "@/dentlab/src/components/client/client-preview-hover-card.component";
import { Star } from "lucide-react";
import { Logger } from "@/lib/logger/Logger";
import { useUploadGuard } from "@/dentlab/src/hooks/useUploadGuard";
import { UnreadIndicator } from "@/dentlab/src/components/chat/unread-indicator.component";

interface ClientSidebarItemProps {
	client: ClientEntityType & { image_src?: string };
	isSelected: boolean;
	expanded: boolean;
	showCode?: boolean;
	onClientInteraction?: () => void;
	unreadCount?: number;
}

export const ClientSidebarItem: React.FC<ClientSidebarItemProps> = ({
	client,
	isSelected,
	expanded,
	showCode = false,
	onClientInteraction,
	unreadCount,
}) => {
	const navigate = useNavigate();
	const checkNavigationAllowed = useUploadGuard();

	const handleClick = () => {
		if (!checkNavigationAllowed()) return;

		onClientInteraction?.();
		navigate(`/client/${client.id}`);
	};

	const handleJobCreated = useCallback(
		(jobId: number) => {
			if (!checkNavigationAllowed()) return;

			navigate(`/client/${client.id}/job/${jobId}`);
		},
		[navigate, client.id, checkNavigationAllowed]
	);

	const handleJobSelect = useCallback(
		(jobId: number) => {
			if (!checkNavigationAllowed()) return;

			navigate(`/client/${client.id}/job/${jobId}`);
		},
		[navigate, client.id, checkNavigationAllowed]
	);

	const sidebarItem = (
		<div
			onClick={handleClick}
			className={cn(
				"flex items-center gap-2 py-1 rounded-md cursor-pointer transition-colors duration-200 text-gray-700",
				expanded ? "px-2 justify-start" : "justify-center px-0",
				isSelected ? "bg-stone-300 font-medium" : "hover:bg-stone-300"
			)}
			data-testid="client-sidebar-item"
		>
			<div className="relative flex-shrink-0">
				<ClientInitialsAvatar
					client={client}
					showCode={showCode}
					expanded={expanded}
				/>
				{!expanded && (
					<UnreadIndicator
						count={unreadCount}
						showCount={false}
						className="absolute -top-[1px] -right-[1px]"
					/>
				)}
			</div>

			{expanded && (
				<>
					<div className="flex items-center gap-1 min-w-0 flex-1">
						<span
							className={cn(
								"text-sm truncate",
								client.favorite ? "font-bold" : "font-medium"
							)}
						>
							{formatClientNameForSidebar(
								client.first_name,
								client.last_name,
								client.company_name,
								client.is_company
							)}
						</span>
						{client.favorite && (
							<Star
								size={12}
								className="text-yellow-500 fill-yellow-400 flex-shrink-0"
							/>
						)}
					</div>
					<UnreadIndicator
						count={unreadCount}
						showCount={true}
						className="flex-shrink-0"
					/>
				</>
			)}
		</div>
	);

	// Only wrap with hover card when sidebar is expanded
	if (expanded) {
		if (!client.id) {
			Logger.error(
				"Client ID not found when creating client preview hover card"
			);
			return sidebarItem;
		}
		return (
			<ClientPreviewHoverCard
				clientId={client.id}
				onJobCreated={handleJobCreated}
				onJobSelect={handleJobSelect}
			>
				{sidebarItem}
			</ClientPreviewHoverCard>
		);
	}

	return sidebarItem;
};
